<template>
  <div class="not-found-container">
    <div class="content-wrapper">
      <!-- 3D Scene Container -->
      <div class="scene-container">
        <div class="floating-404">404</div>
        <div class="astronaut">
          <div class="astronaut-helmet"></div>
          <div class="astronaut-body"></div>
          <div class="astronaut-pack"></div>
        </div>
        <div class="stars"></div>
      </div>

      <!-- Content Section -->
      <div class="text-content">
        <h1 class="title">Houston, we have a problem!</h1>
        <p class="description">
          The page you're looking for has drifted into deep space. Don't worry
          though, we can help you get back on track!
        </p>

        <!-- Search Bar -->
        <div class="search-container">
          <input
            type="text"
            placeholder="Search for pages..."
            class="search-input"
            v-model="searchQuery"
            @keyup.enter="handleSearch"
          />
          <button class="search-button" @click="handleSearch">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <router-link to="/" class="primary-button">
            <span>Return Home</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </router-link>
          <button class="secondary-button" @click="goBack">
            <span>Go Back</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
        </div>

        <!-- Suggested Links -->
        <div class="suggested-links">
          <h3>You might be interested in:</h3>
          <div class="links-grid">
            <router-link
              v-for="link in suggestedLinks"
              :key="link.path"
              :to="link.path"
              class="suggested-link"
            >
              {{ link.name }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "NotFoundPage",
  data() {
    return {
      searchQuery: "",
      suggestedLinks: [
        { name: "Dashboard", path: "/dashboard" },
        { name: "Profile", path: "/profile" },
        { name: "Settings", path: "/settings" },
        { name: "Help Center", path: "/help" },
      ],
    };
  },
  methods: {
    handleSearch() {
      // Implement search functionality
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    // Initialize any animations or 3D effects
    this.initializeAnimations();
  },
  methods: {
    initializeAnimations() {
      // Add animation logic here
      const astronaut = document.querySelector(".astronaut");
      if (astronaut) {
        astronaut.style.animation = "float 6s ease-in-out infinite";
      }
    },
  },
};
</script>

<style scoped>
.not-found-container {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  overflow: hidden;
  position: relative;
}

.content-wrapper {
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
}

/* 3D Scene Styles */
.scene-container {
  position: relative;
  width: 100%;
  height: 300px;
  margin-bottom: 2rem;
}

.floating-404 {
  font-size: 12rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.1);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 4s ease-in-out infinite;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
}

.astronaut {
  width: 120px;
  height: 160px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: float 6s ease-in-out infinite;
}

.astronaut-helmet {
  width: 60px;
  height: 60px;
  background: #fff;
  border-radius: 50%;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
}

.astronaut-body {
  width: 80px;
  height: 100px;
  background: #fff;
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 40px;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
}

.stars {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(
      2px 2px at 20px 30px,
      #fff,
      rgba(0, 0, 0, 0)
    ),
    radial-gradient(2px 2px at 40px 70px, #fff, rgba(0, 0, 0, 0)),
    radial-gradient(2px 2px at 50px 160px, #fff, rgba(0, 0, 0, 0)),
    radial-gradient(2px 2px at 90px 40px, #fff, rgba(0, 0, 0, 0)),
    radial-gradient(2px 2px at 130px 80px, #fff, rgba(0, 0, 0, 0));
  background-repeat: repeat;
  background-size: 200px 200px;
  animation: twinkle 4s ease-in-out infinite;
}

/* Content Styles */
.text-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #fff 0%, #a3b1c6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.description {
  font-size: 1.1rem;
  color: #a3b1c6;
  margin-bottom: 2rem;
  line-height: 1.6;
}

/* Search Bar Styles */
.search-container {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 500px;
}

.search-input {
  flex: 1;
  padding: 1rem 1.5rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
}

.search-button {
  padding: 1rem;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  justify-content: center;
}

.primary-button,
.secondary-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.primary-button {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  color: #fff;
  border: none;
}

.secondary-button {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.primary-button:hover,
.secondary-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

/* Suggested Links */
.suggested-links {
  margin-top: 2rem;
  text-align: center;
}

.suggested-links h3 {
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: #a3b1c6;
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.suggested-link {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  color: #fff;
  text-decoration: none;
  transition: all 0.3s ease;
}

.suggested-link:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

/* Animations */
@keyframes float {
  0%,
  100% {
    transform: translate(-50%, -50%);
  }
  50% {
    transform: translate(-50%, -60%);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.2;
  }
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0.8;
  }
  50% {
    opacity: 0.4;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .floating-404 {
    font-size: 8rem;
  }

  .title {
    font-size: 2rem;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .primary-button,
  .secondary-button {
    width: 100%;
    justify-content: center;
  }

  .search-container {
    flex-direction: column;
  }

  .search-button {
    width: 100%;
  }
}

@media (min-width: 1024px) {
  .content-wrapper {
    flex-direction: row;
    align-items: center;
    gap: 4rem;
  }

  .scene-container {
    flex: 1;
    height: 500px;
  }

  .text-content {
    flex: 1;
    text-align: left;
  }

  .action-buttons,
  .suggested-links {
    justify-content: flex-start;
  }
}
</style>
